"use client";
import React, { useRef, useEffect, useState } from "react";
import { createPortal } from 'react-dom';
import { motion, useInView } from "framer-motion";




const VideoGridSection = (props) => {
  const [videosData, setVideosData] = React.useState([]);
  const sectionRef = useRef(null);
  const fetchRef = React.useRef(null);
  const isInView = useInView(sectionRef, { once: false });

  React.useEffect(() => {
    // Load videos initially and then poll for updates. Also refresh on visibility/focus.
    if (Array.isArray(props.data) && props.data.length > 0) {
      const isVideoLocal = (v) => {
        try { if (v?.type && String(v.type).toLowerCase().startsWith('video')) return true; } catch (e) { }
        const u = String(v?.url || v?.name || v?.publicId || '').toLowerCase();
        return /\.(mp4|mov|webm|mkv|ogg|ogv|m4v)(\?.*)?$/.test(u);
      };
      setVideosData(props.data.filter(v => v && isVideoLocal(v)));
      return;
    }

    const fetchInstagram = () => fetch('/api/instagram').then(res => res.json()).catch(() => []);
    const fetchFromR2 = () => fetch('/api/media-storage').then(res => res.json()).catch(() => null);
    const fetchFallback = () => fetch('/api/shared-media').then(res => res.json()).catch(() => []);

    let mounted = true;
    async function fetchAndSet() {
      try {
        let mediaList = await fetchInstagram();
        if (!Array.isArray(mediaList) || mediaList.length === 0) {
          mediaList = await fetchFromR2();
        }
        if (!Array.isArray(mediaList) || mediaList.length === 0) {
          mediaList = await fetchFallback();
        }

        const isVideoItem = (item) => {
          try {
            const t = String(item?.type || '').toLowerCase();
            if (t && t.startsWith('video')) return true;
          } catch (e) { }
          const u = String(item?.url || item?.name || item?.publicId || '').toLowerCase();
          return /\.(mp4|mov|webm|mkv|ogg|ogv|m4v)(\?.*)?$/.test(u);
        };

        if (mounted) {
          const videosOnly = (mediaList || []).filter(item => isVideoItem(item));
          videosOnly.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
          setVideosData(videosOnly);
        }
      } catch (e) {
        if (mounted) setVideosData([]);
      }
    }

    fetchAndSet();
    // expose to ref so intersection observer can trigger a refresh when section enters view
    fetchRef.current = fetchAndSet;

    return () => { mounted = false; };
  }, [props.data]);

  // Carousel logic
  const [centerIdx, setCenterIdx] = React.useState(0);
  const scrollRef = React.useRef(null);
  const videoRefs = React.useRef([]);
  const overlayRef = React.useRef(null);
  const [overlayLeft, setOverlayLeft] = React.useState(null);
  // bump this to force-remount the centered <video> when browsers show black frames
  const [remountKey, setRemountKey] = React.useState(0);
  const [isMobile, setIsMobile] = React.useState(false);

  // Strong remount: when remountKey changes, replace landscape <video> elements
  // Remount when exiting fullscreen or when landscape videos need a refresh
  React.useEffect(() => {
    // We rely on the `key` prop on the video/container to trigger remounting
    // This effect is now just a placeholder/trigger if we need additional logic
  }, [remountKey]);

  // When the section becomes visible, smoothly scroll it into view (full-screen) once
  React.useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    // detect mobile once and on resize
    const detectMobile = () => {
      try {
        const mq = window.matchMedia ? window.matchMedia('(max-width: 767px)') : null;
        setIsMobile(mq ? mq.matches : (window.innerWidth < 768));
      } catch (e) { }
    };
    detectMobile();
    window.addEventListener('resize', detectMobile);

    let triggered = false;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.35 && !triggered) {
          triggered = true;
          // On mobile we don't force-scroll the section to the top (disable sticky behavior)
          const isMobile = typeof window !== 'undefined' && (window.matchMedia ? window.matchMedia('(max-width: 767px)').matches : window.innerWidth < 768);
          if (!isMobile) {
            try {
              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } catch (e) {
              window.scrollTo({ top: el.offsetTop, behavior: 'smooth' });
            }
          }
          // Refresh videos when user enters the section (always)
          if (fetchRef.current) fetchRef.current();
        } else if (!entry.isIntersecting && triggered) {
          // reset only once the section has fully left the viewport
          triggered = false;
        }
      });
    }, { threshold: [0, 0.15, 0.35, 0.5] });
    io.observe(el);
    return () => { io.disconnect(); window.removeEventListener('resize', detectMobile); };
  }, []);

  // Scroll to idx
  const [remainingSec, setRemainingSec] = React.useState(10);
  const [isPaused, setIsPaused] = React.useState(false);

  const scrollToIdx = (idx) => {
    setCenterIdx(idx);
  };
  const selectIdx = scrollToIdx;

  const next = () => {
    if (videosData.length) scrollToIdx((centerIdx + 1) % videosData.length);
  };

  const prev = () => {
    if (videosData.length) scrollToIdx((centerIdx - 1 + videosData.length) % videosData.length);
  };

  // Autoplay center video, keep others playing muted to avoid black flash on switch
  React.useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      v.loop = false;
      if (i === centerIdx) {
        v.muted = true;
        try {
          // لا تعيد التشغيل من الصفر إن كان الفيديو جاهز حتى لا يظهر فريم أسود، ابدأ فقط إن كان في البداية
          if (typeof v.currentTime !== 'undefined' && v.currentTime < 0.05) {
            v.currentTime = 0.05;
          }
        } catch (e) { }
        v.play().catch(() => { });
      } else {
        // ابقِ الفيديوهات الأخرى تعمل مكتومة لتجنب السواد عند الانتقال السريع
        try { v.muted = true; v.play().catch(() => { }); } catch (e) { }
      }
    });
  }, [centerIdx, videosData]);

  // when a centered video ends, exit fullscreen and stop playback
  React.useEffect(() => {
    const v = videoRefs.current[centerIdx];
    if (!v) return;
    const onEnded = () => {
      try {
        if (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement) {
          if (document.exitFullscreen) document.exitFullscreen();
          else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        }
      } catch (e) { }
      try { v.pause(); if (typeof v.currentTime !== 'undefined') v.currentTime = 0; } catch (e) { }
    };
    v.addEventListener('ended', onEnded);
    return () => v.removeEventListener('ended', onEnded);
  }, [centerIdx]);

  // Pause playback when the page is hidden or when exiting fullscreen (prevent background play)
  React.useEffect(() => {
    const onVisibility = () => {
      try {
        if (document.hidden) {
          videoRefs.current.forEach(v => { if (v) try { v.pause(); } catch (e) { } });
        } else {
          const v = videoRefs.current[centerIdx];
          if (v) try { v.muted = true; v.play().catch(() => { }); } catch (e) { }
        }
      } catch (e) { }
    };
    const onFullChange = () => {
      try {
        const fd = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
        const v = videoRefs.current[centerIdx];
        if (fd) {
          // entered fullscreen — if centered video is portrait, make it contain (no crop)
          try {
            const isMobile = typeof window !== 'undefined' && (window.matchMedia ? window.matchMedia('(max-width: 767px)').matches : window.innerWidth < 768);
            if (v && !isMobile) {
              // save previous inline objectFit so we can restore
              try { v.dataset._prevObjectFit = v.style.objectFit || ''; v.dataset._prevWidth = v.style.width || ''; v.dataset._prevHeight = v.style.height || ''; } catch (e) { }
              v.style.objectFit = 'contain';
              v.style.width = '100%';
              v.style.height = '100%';
            }
          } catch (e) { }
        } else {
          // exited fullscreen — restore sizing, then reload & resume playback (muted)
          try {
            if (v) {
              if (typeof v.dataset._prevObjectFit !== 'undefined') v.style.objectFit = v.dataset._prevObjectFit || '';
              if (typeof v.dataset._prevWidth !== 'undefined') v.style.width = v.dataset._prevWidth || '';
              if (typeof v.dataset._prevHeight !== 'undefined') v.style.height = v.dataset._prevHeight || '';
              try { delete v.dataset._prevObjectFit; delete v.dataset._prevWidth; delete v.dataset._prevHeight; } catch (e) { }
              // force a reload and play to ensure frames render in the section (fix black gap)
              try { v.pause(); } catch (e) { }
              try { v.muted = true; v.load(); } catch (e) { }
              setTimeout(() => { try { v.play().catch(() => { }); } catch (e) { } }, 80);
              // bump remount key to force recreating the DOM node if browser keeps a black frame
              try { setRemountKey(k => (k || 0) + 1); } catch (e) { }
            }
          } catch (e) { }
        }
      } catch (e) { }
    };
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('fullscreenchange', onFullChange);
    document.addEventListener('webkitfullscreenchange', onFullChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('fullscreenchange', onFullChange);
      document.removeEventListener('webkitfullscreenchange', onFullChange);
    };
  }, [centerIdx]);

  // Countdown-driven auto-advance (10 seconds)
  React.useEffect(() => {
    if (videosData.length <= 1) return;
    setRemainingSec(10);
  }, [videosData, centerIdx]);

  React.useEffect(() => {
    if (videosData.length <= 1) return;
    // if paused and not fullscreen, don't run countdown
    const isCurrentFullscreen = (() => {
      try {
        const fd = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
        const v = videoRefs.current[centerIdx];
        return !!(fd && v && (fd === v || (fd.contains && fd.contains(v)) || (v.contains && v.contains(fd))));
      } catch (e) { return false; }
    })();
    if (isPaused && !isCurrentFullscreen) return;
    const t = setInterval(() => {
      setRemainingSec(s => {
        // recompute fullscreen inside interval in case it changed
        let nowFull = false;
        try {
          const fd = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
          const v = videoRefs.current[centerIdx];
          nowFull = !!(fd && v && (fd === v || (fd.contains && fd.contains(v)) || (v.contains && v.contains(fd))));
        } catch (e) { nowFull = false; }
        if (nowFull) {
          // don't decrement or auto-advance while fullscreen
          return s;
        }
        if (s <= 1) {
          const nextIdx = (centerIdx + 1) % videosData.length;
          scrollToIdx(nextIdx);
          return 10;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [videosData, isPaused, centerIdx]);

  // Ensure initial card is centered when videos load
  React.useEffect(() => {
    if (!videosData.length) return;
    const t = setTimeout(() => scrollToIdx(centerIdx), 80);
    return () => clearTimeout(t);
  }, [videosData]);

  // Preload all videos on mount to avoid flicker when switching
  React.useEffect(() => {
    if (!videosData || videosData.length === 0) return;
    videoRefs.current.forEach((v) => {
      if (!v) return;
      try {
        v.preload = 'auto';
        v.load();
      } catch (e) { }
    });
  }, [videosData]);

  // Defensive reload: when videosData changes, ensure each <video> element has correct src, is loaded,
  // looping disabled, and the centered video plays from start while others are paused.
  React.useEffect(() => {
    if (!videosData || videosData.length === 0) return;
    const t = setTimeout(() => {
      videoRefs.current.forEach((v, i) => {
        try {
          if (!v) return;
          // patch src if changed
          const newUrl = videosData[i] && videosData[i].url;
          if (newUrl && v.src !== newUrl) {
            try { v.removeAttribute && v.removeAttribute('src'); } catch (e) { }
            try { v.load && v.load(); } catch (e) { }
            v.src = newUrl;
          }
          v.loop = false;
          v.preload = 'auto';
          try { v.load(); } catch (e) { }
          if (i === centerIdx) {
            try { if (typeof v.currentTime !== 'undefined') v.currentTime = 0; } catch (e) { }
            v.muted = true;
            v.play().catch(() => { });
          } else {
            try { v.pause(); } catch (e) { }
          }
        } catch (e) { }
      });
    }, 60);
    return () => clearTimeout(t);
  }, [videosData]);

  // Detect orientation (landscape vs portrait) for each video so we can adjust sizing
  const [videoOrientations, setVideoOrientations] = React.useState([]);
  React.useEffect(() => {
    if (!videosData || videosData.length === 0) { setVideoOrientations([]); return; }
    // No orientation check
    const update = () => {
      // setOverlayLeft(null);
    };
    const t = setTimeout(() => {
      update();
      scrollToIdx(centerIdx);
    }, 120);
    window.addEventListener('resize', update);
    return () => { clearTimeout(t); window.removeEventListener('resize', update); };
  }, [videosData, centerIdx]);

  // Position overlay above the centered card (videos remain in place)
  React.useEffect(() => {
    const compute = () => {
      const sec = sectionRef.current;
      const v = videoRefs.current[centerIdx];
      if (!sec || !v) return;
      const secRect = sec.getBoundingClientRect();
      const vidRect = v.getBoundingClientRect();
      const left = vidRect.left - secRect.left + vidRect.width / 2;
      setOverlayLeft(left);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [centerIdx, videosData]);

  // whether the centered video is landscape (prefer real dimensions, fallback to stored orientation)
  const centerVideoEl = videoRefs.current[centerIdx];
  const centerIsLandscape = centerVideoEl && centerVideoEl.videoWidth && centerVideoEl.videoHeight
    ? centerVideoEl.videoWidth > centerVideoEl.videoHeight
    : videoOrientations[centerIdx] === 'landscape';

  // suppress animations for non-center items briefly when exiting a centered landscape
  const prevCenterIsLandscapeRef = React.useRef(centerIsLandscape);
  const prevCenterWasLandscape = prevCenterIsLandscapeRef.current;
  const [suppressExitAnimations, setSuppressExitAnimations] = React.useState(false);
  React.useEffect(() => {
    const prev = prevCenterIsLandscapeRef.current;
    if (prev && !centerIsLandscape) {
      // we just exited a landscape center — disable animations on all cards briefly
      setSuppressExitAnimations(true);
      const t = setTimeout(() => setSuppressExitAnimations(false), 60);
      prevCenterIsLandscapeRef.current = centerIsLandscape;
      return () => clearTimeout(t);
    }
    prevCenterIsLandscapeRef.current = centerIsLandscape;
  }, [centerIsLandscape]);

  return (
    <section ref={sectionRef} data-hide-native-cursor className="bg-black overflow-hidden relative" style={{ minHeight: isMobile ? 'auto' : '100vh' }}>
      <div className="flex flex-row items-center justify-center w-full relative" style={{ height: isMobile ? 'auto' : '100vh', minHeight: isMobile ? 'auto' : '100vh' }}>
        <div
          className="w-full overflow-hidden"
          style={{ position: 'relative', height: '100%' }}
        >
          <div className="flex" style={{ width: '100%', height: '100%' }}>
            {(() => {
              const len = videosData.length;
              if (len === 0) return null;
              const prevIdx = (centerIdx - 1 + len) % len;
              const nextIdx = (centerIdx + 1) % len;
              // عند landscape في المركز: عرض المركز فقط بكامل العرض
              // عند portrait: عرض السابق والمركز والتالي
              const indices = isMobile
                ? [centerIdx]
                : (len >= 3 ? [prevIdx, centerIdx, nextIdx] : [...Array(len).keys()]);
              return indices.map((idx, pos) => {
                const isCenter = idx === centerIdx;
                const video = videosData[idx];
                const isLandscape = videoOrientations[idx] === 'landscape';
                const isCenterLandscape = centerIsLandscape;
                const portraitFrame = isMobile ? 'auto' : '100vh';
                const slicePortrait = !isCenterLandscape && isLandscape && !isCenter;
                const videoEl = videoRefs.current[idx];
                const metaAspect = (videoEl && videoEl.videoWidth && videoEl.videoHeight)
                  ? `${videoEl.videoWidth} / ${videoEl.videoHeight}`
                  : undefined;
                const cardHeight = isMobile ? 'auto' : portraitFrame;
                const cardAspect = isLandscape ? '9 / 16' : (slicePortrait ? '9 / 16' : metaAspect);
                const cardMaxHeight = isMobile ? '100vh' : portraitFrame;
                const hideSide = !isMobile && centerIsLandscape && !isCenter;
                // عند الموبايل: بطاقة واحدة بعرض كامل. الديسكتوب: العرض بحسب الحالة مع إخفاء الجوانب في العرضي فقط مع انتقال سلس.
                const cardWidth = isMobile
                  ? '100%'
                  : hideSide
                    ? '0%'
                    : (isCenterLandscape && isCenter ? '100%' : '33.3333%');
                const cardOpacity = hideSide ? 0 : 1;
                const cardPointerEvents = hideSide ? 'none' : 'auto';
                const cardTransition = 'width 420ms cubic-bezier(.2,.8,.2,1), flex 420ms cubic-bezier(.2,.8,.2,1), opacity 420ms cubic-bezier(.2,.8,.2,1)';
                return (
                  <div
                    key={(video?.url || video?.name || idx) + '-' + remountKey}
                    onClick={() => selectIdx(idx)}
                    onMouseEnter={isCenter ? () => setIsPaused(true) : undefined}
                    onMouseLeave={isCenter ? () => setIsPaused(false) : undefined}
                    onTouchStart={isCenter ? () => setIsPaused(true) : undefined}
                    onTouchEnd={isCenter ? () => setIsPaused(false) : undefined}
                    style={{
                      cursor: 'pointer',
                      width: cardWidth,
                      flex: `0 0 ${cardWidth}`,
                      scrollSnapAlign: 'center',
                      transition: cardTransition,
                      transform: 'none',
                      transformOrigin: 'center center',
                      opacity: cardOpacity,
                      pointerEvents: cardPointerEvents,
                      willChange: 'transform, opacity',
                      WebkitBackfaceVisibility: 'hidden',
                      backfaceVisibility: 'hidden',
                      flexShrink: 0,
                      height: cardHeight,
                      maxHeight: cardMaxHeight,
                      aspectRatio: cardAspect,
                      overflow: 'hidden',
                      backgroundColor: '#111',
                      position: 'relative',
                    }}
                    className={
                      "overflow-hidden bg-gray-900 relative " +
                      (isCenter ? "z-10 cursor-dot-only" : "")
                    }
                  >
                    <div
                      className={'w-full h-full relative'}
                      style={{
                        display: isMobile ? 'flex' : 'block',
                        alignItems: isMobile ? 'center' : 'stretch',
                        justifyContent: isMobile ? 'center' : 'flex-start',
                        overflow: 'hidden'
                      }}
                    >
                      <video
                        ref={el => (videoRefs.current[idx] = el)}
                        src={video?.url}
                        preload="auto"
                        muted
                        playsInline
                        poster=""
                        style={{
                          width: isMobile && !isLandscape ? 'auto' : '100%',
                          height: isMobile && !isLandscape ? 'auto' : '100%',
                          maxHeight: '100%',
                          maxWidth: '100%',
                          objectFit: isMobile && !isLandscape ? 'contain' : 'cover',
                          objectPosition: slicePortrait ? (pos === 0 ? 'right center' : 'left center') : 'center',
                          outline: 'none',
                          backgroundColor: 'black',
                          display: 'block'
                        }}
                        tabIndex={-1}
                        onLoadedMetadata={e => {
                          try {
                            // Seek small offset to ensure non-black frame
                            e.target.currentTime = 0.1;
                            if (isCenter) e.target.play().catch(() => { });
                            else e.target.pause();
                            // سجل الاتجاه فور توفر الأبعاد
                            const vw = e.target.videoWidth || 0;
                            const vh = e.target.videoHeight || 0;
                            if (vw && vh) {
                              setVideoOrientations(prev => {
                                const next = Array.isArray(prev) ? [...prev] : [];
                                next[idx] = vw > vh ? 'landscape' : 'portrait';
                                return next;
                              });
                            }
                          } catch (err) { }
                        }}
                      />

                    </div>
                  </div>
                );
              });
            })()}
          </div>
          {/* overlay (timer + play) that moves to the centered card */}
          {/** overlay rendered inside the section so it stays scoped and doesn't attach to document.body */}
          {isInView && (overlayLeft != null || centerIsLandscape) && (
            <div
              aria-hidden
              className="absolute top-4 pointer-events-none"
              style={{ left: centerIsLandscape ? '50%' : overlayLeft, transform: 'translateX(-50%)', zIndex: 9999 }}
            >
              <div className="video-timer-bg bg-black/60 text-white px-2 py-1 rounded-md text-xs">
                {remainingSec > 0 ? `${remainingSec}` : `0`}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Controls rendered inside the section so they're fixed to the section area */}
      {isInView && (
        <>
          {/* Play fullscreen button for centered video */}
          <button
            onClick={async () => {
              const v = videoRefs.current[centerIdx];
              if (!v) return;
              try {
                // user-initiated unmute and restart
                v.muted = false;
                try { if (typeof v.currentTime !== 'undefined') v.currentTime = 0; } catch (e) { }
                await v.play();
              } catch (e) { }
              try {
                if (v.requestFullscreen) await v.requestFullscreen();
                else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
                else if (v.webkitRequestFullscreen) await v.webkitRequestFullscreen();
              } catch (e) { }
            }}
            aria-label="تشغيل ملء الشاشة"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white/10 dark:bg-black/30 backdrop-blur-sm border border-white/10 dark:border-white/5 rounded-full p-2 text-white shadow-md opacity-70 hover:opacity-100 transition-opacity"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v18l15-9L5 3z" />
            </svg>
          </button>
          <button onClick={next} aria-label="Next" className="absolute left-4 top-1/2 -translate-y-1/2 z-50 bg-white/10 dark:bg-black/30 backdrop-blur-sm border border-white/10 dark:border-white/5 rounded-full p-2 text-white shadow-md opacity-70 hover:opacity-100 transition-opacity">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button onClick={prev} aria-label="Previous" className="absolute right-4 top-1/2 -translate-y-1/2 z-50 bg-white/10 dark:bg-black/30 backdrop-blur-sm border border-white/10 dark:border-white/5 rounded-full p-2 text-white shadow-md opacity-70 hover:opacity-100 transition-opacity">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <div className="absolute left-1/2 bottom-4 -translate-x-1/2 z-50">
            <div className="flex justify-center gap-2 mt-4">
              {Array.from({ length: videosData.length }).map((_, idx) => (
                <button
                  key={idx}
                  className={"w-2 h-2 rounded-full " + (idx === centerIdx ? "bg-[#dfa428]" : "bg-transparent border border-white/80")}
                  onClick={() => selectIdx(idx)}
                  aria-label={`Go to video ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
};
// End of VideoGridSection component



const VideoCard = ({ video, index, isInView }) => {
  return (
    <motion.div
      className="group relative overflow-hidden h-full"
      initial={{ opacity: 0, scale: 0.8, y: 100 }}
      animate={isInView ? { opacity: 1, scale: 1, y: 0 } : {}}
      transition={{ duration: 1, delay: index * 0.2 }}
      whileHover={{ scale: 1.05, z: 10 }}
    >
      <div className="relative h-full overflow-hidden">
        {video.url ? (
          <motion.video
            src={video.url}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-contain"
            whileHover={{ scale: 1.1 }}
            transition={{ duration: 0.8 }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-900 text-white">
            الفيديو غير متوفر
          </div>
        )}

        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all duration-500"></div>

        <motion.div
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          initial={{ scale: 0.5 }}
          whileHover={{ scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="text-center text-white">
            <motion.div
              className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 mx-auto"
              whileHover={{ scale: 1.2, rotate: 360 }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-0 h-0 border-l-[16px] border-l-white border-t-[12px] border-t-transparent border-b-[12px] border-b-transparent ml-1"></div>
            </motion.div>
            <motion.h3
              className="text-xl font-bold"
              initial={{ y: 20, opacity: 0 }}
              whileHover={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              {video.title}
            </motion.h3>
          </div>
        </motion.div>

        <motion.div
          className="absolute top-4 right-4 w-3 h-3 bg-[#dfa428] rounded-full opacity-0 group-hover:opacity-100"
          animate={{ scale: [1, 1.5, 1], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
        />
        <motion.div
          className="absolute bottom-4 left-4 w-2 h-2 bg-white rounded-full opacity-0 group-hover:opacity-100"
          animate={{ scale: [1.5, 1, 1.5], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 3, repeat: Infinity, delay: index * 0.4 }}
        />

        <motion.div
          className="absolute inset-0 border-2 border-[#dfa428]/0 group-hover:border-[#dfa428]/50 transition-all duration-500"
          whileHover={{
            boxShadow: "0 0 30px rgba(255, 193, 7, 0.5)",
          }}
        />
      </div>
    </motion.div>
  );
};

export default VideoGridSection;

function PortalOverlay({ children, refEl }) {
  const [el] = useState(() => typeof document !== 'undefined' ? document.createElement('div') : null);

  useEffect(() => {
    if (!el) return;
    el.style.position = 'fixed';
    el.style.bottom = '16px';
    el.style.left = '50%';
    el.style.transform = 'translateX(-50%)';
    el.style.zIndex = '99999';
    el.style.pointerEvents = 'none';
    document.body.appendChild(el);
    return () => { document.body.removeChild(el); };
  }, [el]);

  if (!el) return null;
  return createPortal(children, el);
}

function PortalControls({ onPrev, onNext, length, centerIdx, selectIdx }) {
  const [el] = React.useState(() => (typeof document !== 'undefined' ? document.createElement('div') : null));

  React.useEffect(() => {
    if (!el) return;
    el.style.position = 'fixed';
    el.style.top = '50%';
    el.style.left = '16px';
    el.style.transform = 'translateY(-50%)';
    el.style.zIndex = '100001';
    document.body.appendChild(el);
    return () => { document.body.removeChild(el); };
  }, [el]);

  const [elR] = React.useState(() => (typeof document !== 'undefined' ? document.createElement('div') : null));
  React.useEffect(() => {
    if (!elR) return;
    elR.style.position = 'fixed';
    elR.style.top = '50%';
    elR.style.right = '16px';
    elR.style.transform = 'translateY(-50%)';
    elR.style.zIndex = '100001';
    document.body.appendChild(elR);
    return () => { document.body.removeChild(elR); };
  }, [elR]);

  const [elDots] = React.useState(() => (typeof document !== 'undefined' ? document.createElement('div') : null));
  React.useEffect(() => {
    if (!elDots) return;
    elDots.style.position = 'fixed';
    elDots.style.left = '50%';
    elDots.style.bottom = '96px';
    elDots.style.transform = 'translateX(-50%)';
    elDots.style.zIndex = '100001';
    document.body.appendChild(elDots);
    return () => { document.body.removeChild(elDots); };
  }, [elDots]);

  if (!el || !elR || !elDots) return null;

  return (
    <>
      {createPortal(
        <button onClick={onPrev} aria-label="Previous" className="bg-white/10 dark:bg-black/30 backdrop-blur-sm border border-white/10 dark:border-white/5 rounded-full p-2 text-white shadow-md opacity-70 hover:opacity-100 transition-opacity">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>,
        el
      )}
      {createPortal(
        <button onClick={onNext} aria-label="Next" className="bg-white/10 dark:bg-black/30 backdrop-blur-sm border border-white/10 dark:border-white/5 rounded-full p-2 text-white shadow-md opacity-70 hover:opacity-100 transition-opacity">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>,
        elR
      )}
      {createPortal(
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length }).map((_, idx) => (
            <button
              key={idx}
              className={"w-3 h-3 rounded-full " + (idx === centerIdx ? "bg-[#dfa428]" : "bg-gray-300")}
              onClick={() => selectIdx(idx)}
              aria-label={`Go to video ${idx + 1}`}
            />
          ))}
        </div>,
        elDots
      )}
    </>
  );
}
